import { LogOut, Timer } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
// import GoogleButton from 'react-google-button';
import { Link } from 'react-router';

import { Button } from '@/components/ui/button';
import { GOOGLE_SIGNIN_URL } from '@/config/endpoints';
import { paths } from '@/config/paths';
import { cn } from '@/utils/cn';

import { ModalBase } from './modal-base';

type TokenExpireTimeAlertProps = {
  phase: 'PROD' | 'BETA' | 'ALPHA';
  isLoggedIn: boolean;
  tokenExpireTime: number;
  thresholdMs: number;
};

const TokenExpireTimeAlert = ({
  phase,
  isLoggedIn,
  tokenExpireTime,
  thresholdMs,
}: TokenExpireTimeAlertProps) => {
  const [isAboutToExpire, setIsAboutToExpire] = useState(false);

  const checkStatus = useCallback(() => {
    if (isLoggedIn) {
      const currentTime = Date.now();
      const remaining = tokenExpireTime - currentTime;
      setIsAboutToExpire(0 < remaining && remaining < thresholdMs);
    } else if (isAboutToExpire) {
      setIsAboutToExpire(false);
    }
  }, [isLoggedIn, isAboutToExpire, tokenExpireTime, thresholdMs]);

  // Check the current time and expire time every 15 seconds.
  useEffect(() => {
    checkStatus();

    const timerId = setInterval(() => checkStatus(), 1000 * 15);
    return () => clearInterval(timerId);
  }, [checkStatus]);

  return (
    <ModalBase
      className={cn(
        'z-max',
        isAboutToExpire
          ? ['display', 'opacity-100']
          : ['invisible', 'opacity-0'],
      )}
    >
      <div
        className={cn(
          ['text-2xl', 'font-bold'],
          ['border-b-2', 'border-stone-300', 'border-dashed'],
          ['flex', 'flex-row', 'items-center', 'gap-2'],
        )}
      >
        <Timer className="shrink-0" />
        <span>This login session is about to expire in 5 minutes!</span>
      </div>

      <div className="text-xl">
        Please try logging into GlyTouCan again with your Google Account below.
      </div>

      <div
        className={cn('px-4', [
          'flex',
          'flex-row',
          'gap-4',
          'justify-around',
          'flex-wrap',
          'items-center',
        ])}
      >
        <a href={GOOGLE_SIGNIN_URL[phase]}>
          <span className="inline-block cursor-not-allowed">
            <button
              type="button"
              className="box-border inline-flex items-center rounded-md border border-transparent bg-[#4285F4] px-4 py-2.5 text-center text-sm font-medium leading-5 text-white hover:bg-[#4285F4]/90 focus:outline-none focus:ring-4 focus:ring-[#4285F4]/50 dark:focus:ring-[#4285F4]/55"
            >
              <svg
                className="me-1.5 size-4"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  fillRule="evenodd"
                  d="M12.037 21.998a10.313 10.313 0 0 1-7.168-3.049 9.888 9.888 0 0 1-2.868-7.118 9.947 9.947 0 0 1 3.064-6.949A10.37 10.37 0 0 1 12.212 2h.176a9.935 9.935 0 0 1 6.614 2.564L16.457 6.88a6.187 6.187 0 0 0-4.131-1.566 6.9 6.9 0 0 0-4.794 1.913 6.618 6.618 0 0 0-2.045 4.657 6.608 6.608 0 0 0 1.882 4.723 6.891 6.891 0 0 0 4.725 2.07h.143c1.41.072 2.8-.354 3.917-1.2a5.77 5.77 0 0 0 2.172-3.41l.043-.117H12.22v-3.41h9.678c.075.617.109 1.238.1 1.859-.099 5.741-4.017 9.6-9.746 9.6l-.215-.002Z"
                  clipRule="evenodd"
                />
              </svg>
              Sign in with Google
            </button>
          </span>
        </a>

        <Link to={paths.auth.logout.getHref()}>
          <Button
            size="lg"
            className="transition-all duration-500 focus:ring-2"
          >
            <LogOut />
            Logout
          </Button>
        </Link>
      </div>
    </ModalBase>
  );
};

TokenExpireTimeAlert.displayName = 'TokenExpireTimeAlert';

export { TokenExpireTimeAlert };
